import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as BookingService from "_services/booking-service";
import * as BookingAnalyticsService from "_services/bookingAnalytics-service";

export const getDepartureBookings = createAsyncThunk(
  "security/getDepartureBookings",
  async ({ iata }) => {
    const today = new Date().toISOString().split("T")[0];
    return BookingService.getBookingDocuments({
      iata,
      start: today,
      end: today,
      type: "outbound",
      limit: 10000,
      skip: 0,
      search: "",
    });
  }
);

export const getHourlyForecast = createAsyncThunk(
  "security/getHourlyForecast",
  async ({ iata, date }) => {
    return BookingAnalyticsService.getHourlyForecast({ iata, date });
  }
);

const initialState = {
  departureBookings: [],
  status: "idle",
  error: null,
  hourlyForecast: null,
  forecastStatus: "idle",
  forecastError: null,
};

const securitySlice = createSlice({
  name: "security",
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: {
    [getDepartureBookings.pending]: (state) => {
      state.status = "loading";
      state.error = null;
    },
    [getDepartureBookings.fulfilled]: (state, action) => {
      state.departureBookings = action.payload;
      state.status = "succeeded";
    },
    [getDepartureBookings.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.error.message;
    },
    [getHourlyForecast.pending]: (state) => {
      state.forecastStatus = "loading";
      state.forecastError = null;
    },
    [getHourlyForecast.fulfilled]: (state, action) => {
      state.hourlyForecast = action.payload;
      state.forecastStatus = "succeeded";
    },
    [getHourlyForecast.rejected]: (state, action) => {
      state.forecastStatus = "failed";
      state.forecastError = action.error.message;
      state.hourlyForecast = null;
    },
  },
});

export const { reset } = securitySlice.actions;

const countBookingsForHour = (bookings, targetHour) => {
  if (!Array.isArray(bookings)) return 0;
  return bookings.reduce((count, booking) => {
    const dep = booking?.doc?.flightConfirmationDetails?.departureTime;
    if (!dep) return count;
    const hour = new Date(dep).getHours();
    return hour === targetHour ? count + 1 : count;
  }, 0);
};

export const selectSecurityStatus = (state) => state.security.status;

export const selectDepartureCountForHour = (hour) => (state) =>
  countBookingsForHour(state.security.departureBookings, hour);

export const selectCurrentHourCount = (mode) => (state) => {
  const now = new Date().getHours();
  const target = mode === "Real Time" ? now + 2 : now;
  return countBookingsForHour(state.security.departureBookings, target);
};

export const selectNextHourCount = (mode) => (state) => {
  const now = new Date().getHours();
  const target = mode === "Real Time" ? now + 3 : now + 1;
  return countBookingsForHour(state.security.departureBookings, target);
};

export const selectDeparturesByHour = (mode) => (state) => {
  const bookings = state.security.departureBookings;
  if (!Array.isArray(bookings)) return [];

  const offset = mode === "Real Time" ? 2 : 0;
  const counts = new Array(24).fill(0);

  bookings.forEach((booking) => {
    const dep = booking?.doc?.flightConfirmationDetails?.departureTime;
    if (!dep) return;
    const flightHour = new Date(dep).getHours();
    const displayHour = flightHour - offset;
    if (displayHour >= 0 && displayHour < 24) {
      counts[displayHour] += 1;
    }
  });

  return counts.map((count, hour) => ({
    name: hour.toString().padStart(2, "0") + ":00",
    Departures: count,
  }));
};

export const selectDeparturesByInterval = (mode, intervalMinutes) => (state) => {
  const bookings = state.security.departureBookings;
  if (!Array.isArray(bookings)) return [];

  const offset = mode === "Real Time" ? 2 : 0;
  const slotsPerDay = (24 * 60) / intervalMinutes;
  const counts = new Array(slotsPerDay).fill(0);

  bookings.forEach((booking) => {
    const dep = booking?.doc?.flightConfirmationDetails?.departureTime;
    if (!dep) return;
    const d = new Date(dep);
    const totalMinutes = (d.getHours() - offset) * 60 + d.getMinutes();
    const slot = Math.floor(totalMinutes / intervalMinutes);
    if (slot >= 0 && slot < slotsPerDay) {
      counts[slot] += 1;
    }
  });

  const pad = (n) => n.toString().padStart(2, "0");
  return counts.map((count, i) => {
    const startMin = i * intervalMinutes;
    const h = Math.floor(startMin / 60);
    const m = startMin % 60;
    return {
      name: `${pad(h)}:${pad(m)}`,
      Departures: count,
    };
  });
};

const formatTime = (date) => {
  const h = date.getHours().toString().padStart(2, "0");
  const m = date.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
};

const getLoadLevel = (prms) => {
  if (prms >= 6) return "High";
  if (prms >= 3) return "Medium";
  return "Low";
};

export const selectUpcomingFlights = (state) => {
  const bookings = state.security.departureBookings;
  if (!Array.isArray(bookings) || bookings.length === 0) return [];

  const now = new Date();
  const windowStart = new Date(now.getTime() - 1 * 60 * 60 * 1000);
  const windowEnd = new Date(now.getTime() + 3 * 60 * 60 * 1000);

  const groups = {};

  bookings.forEach((booking) => {
    const details = booking?.doc?.flightConfirmationDetails;
    if (!details?.departureTime) return;

    const depTime = new Date(details.departureTime);
    if (depTime < windowStart || depTime > windowEnd) return;

    const carrier = details.carrierCode || "";
    const flightNum = details.flightNumber || "";
    const key = carrier + flightNum;
    if (!key) return;

    if (!groups[key]) {
      groups[key] = {
        flight: key,
        destination: details.destination || "",
        depTime,
        std: formatTime(depTime),
        pst: formatTime(new Date(depTime.getTime() - 2 * 60 * 60 * 1000)),
        prms: 0,
        wchc: 0,
      };
    }

    groups[key].prms += 1;

    const assistance = booking?.doc?.assistance;
    if (Array.isArray(assistance) && assistance.some((a) => String(a).toUpperCase().includes("WCHC"))) {
      groups[key].wchc += 1;
    }
  });

  return Object.values(groups)
    .map((g) => ({ ...g, load: getLoadLevel(g.prms) }))
    .sort((a, b) => a.depTime - b.depTime);
};

export const selectHourlyForecast = (state) => state.security.hourlyForecast;
export const selectForecastStatus = (state) => state.security.forecastStatus;

export const selectForecastTotal = (state) => {
  const hourly = state.security.hourlyForecast;
  if (!Array.isArray(hourly) || hourly.length !== 24) return 0;
  return Math.round(hourly.reduce((sum, v) => sum + v, 0));
};

export const selectForecastByHour = (state) => {
  const hourly = state.security.hourlyForecast;
  if (!Array.isArray(hourly) || hourly.length !== 24) return null;

  return hourly.map((value, h) => ({
    name: h.toString().padStart(2, "0") + ":00",
    Forecast: Math.round(value * 10) / 10,
  }));
};

export const selectForecastByQuarter = (state) => {
  const hourly = state.security.hourlyForecast;
  if (!Array.isArray(hourly) || hourly.length !== 24) return null;

  const pad = (n) => n.toString().padStart(2, "0");
  const result = [];

  for (let h = 0; h < 24; h++) {
    const quarterValue = Math.round((hourly[h] / 4) * 10) / 10;
    for (let q = 0; q < 4; q++) {
      result.push({
        name: `${pad(h)}:${pad(q * 15)}`,
        Forecast: quarterValue,
      });
    }
  }

  return result;
};

export default securitySlice.reducer;
